/**
 * usePropertyInfoConfig — the propertyInfo module's ops hook (the ONLY write path).
 *
 * MODULE-CONTRACT §2. The module's state IS its flow DTO at
 * `productConfigs["${cartItemId}|property_info"]`; this hook wraps it with a typed
 * read (field-merged over the default + nested deep-default) and a single `patch`
 * writer, plus the nested-array `sheetOps` CRUD helpers that mirror the legacy
 * constate helpers 1:1 but on plain arrays + array index instead of ImmutableMap
 * keys. No imperative handle, no serialize/hydrate — `serializeDraft` /
 * `hydrateFromDraft` already round-trip `productConfigs`.
 *
 * `patch` is inherently the USER-EDIT boundary — hydration never calls it (§3) —
 * so it un-confirms unconditionally via `markConfigDirty`.
 */

import { useMemo } from 'react'

import { AuthoritiesDocumentDataSource } from 'models/purchaseFlow/AuthoritiesDocumentDTO'
import type {
  LandRegistrationSheetDTO,
  LandRegistrationSheetFloorDTO,
  LandRegistrationSheetParcelDTO
} from 'models/purchaseFlow/AuthoritiesDocumentDTO'
import type { CartItemId } from 'models/purchaseFlowRevamp'

import { usePfStore } from '../../store'
import { makeConfigKey } from '../../store/slices/productConfigSlice'
import { useFlowConfig } from '../useFlowConfig'
import {
  PROPERTY_INFO_DEFAULT,
  makeBlankSheet,
  normalizeSheet,
  propertyInfoIsComplete,
  type GroupedParcels,
  type PropertyInfoDraftDTO
} from './propertyInfo.dto'

/** Nested-array CRUD over `value.landRegistrationSheets`. Each op ends in a
 *  `patch({ landRegistrationSheets })`, which promotes the display-synthesized
 *  blank sheet into the persisted DTO on first interaction (A-09 analogue). */
export interface PropertyInfoSheetOps {
  addSheet(): void
  removeSheet(sheetIndex: number): void
  updateSheet(sheetIndex: number, updates: Partial<Pick<LandRegistrationSheetDTO, 'name' | 'district'>>): void
  addFloor(sheetIndex: number): void
  removeFloor(sheetIndex: number, floorIndex: number): void
  updateFloor(sheetIndex: number, floorIndex: number, updates: Partial<Pick<LandRegistrationSheetFloorDTO, 'hallway'>>): void
  addParcel(sheetIndex: number, floorIndex: number): void
  removeParcel(sheetIndex: number, floorIndex: number, parcelIndex: number): void
  updateParcel(sheetIndex: number, floorIndex: number, parcelIndex: number, updates: Partial<LandRegistrationSheetParcelDTO>): void
  /** Replaces the whole sheet list from a DocEstate map selection + tags the DTO
   *  `dataSource: MAP`. Empty selection resets to a single blank sheet. */
  setSheetsFromMap(grouped: GroupedParcels): void
}

/** The hook's display value guarantees a non-empty `landRegistrationSheets` array
 *  (one blank sheet is synthesized when the persisted DTO has none), so the UI can
 *  `.map` it without a null-guard. The intersection makes that guarantee explicit
 *  on top of the optional wire field. */
export type PropertyInfoDisplayValue = PropertyInfoDraftDTO & {
  landRegistrationSheets: LandRegistrationSheetDTO[]
}

export interface UsePropertyInfoConfig {
  /** Field-complete, deep-defaulted DTO — safe to read every nested field even
   *  against a partial or absent persisted entry. `landRegistrationSheets` is
   *  ALWAYS non-empty (one blank sheet synthesized for display). */
  value: PropertyInfoDisplayValue
  /** Merge-and-write a partial DTO. Idempotency-guarded by `setConfig`; flips the
   *  tab back to unconfirmed via `markConfigDirty`. */
  patch: (partial: Partial<PropertyInfoDraftDTO>) => void
  /** Nested-array land-register CRUD. */
  sheetOps: PropertyInfoSheetOps
  /** Completeness of the RAW persisted DTO — an untouched flow (absent DTO) is
   *  incomplete until the first edit promotes a sheet (A-09). */
  isComplete: boolean
}

const makeBlankFloor = (): LandRegistrationSheetFloorDTO => ({ hallway: '', parcels: [{ number: '' }] })

export function usePropertyInfoConfig(cartItemId: CartItemId): UsePropertyInfoConfig {
  const key = makeConfigKey(cartItemId, 'property_info')
  const { raw, patch, isComplete } = useFlowConfig(cartItemId, 'property_info', PROPERTY_INFO_DEFAULT, propertyInfoIsComplete)

  // DISPLAY value: shallow merge over DEFAULT + nested deep-default. If the
  // persisted DTO has no sheets, synthesize one blank sheet for the UI — never
  // persisted (read-only-mount). Sheets shown to the UI are never empty/undefined.
  const value: PropertyInfoDisplayValue = useMemo(() => ({
    ...PROPERTY_INFO_DEFAULT,
    ...raw,
    landRegistrationSheets: (raw?.landRegistrationSheets?.length
      ? raw.landRegistrationSheets
      : [makeBlankSheet()]
    ).map(normalizeSheet),
  }), [raw])

  // Sheet CRUD operates on the DISPLAY sheets (always ≥1, deep-defaulted) read
  // fresh from the store, then writes the whole array back via `patch`.
  const sheetOps = useMemo<PropertyInfoSheetOps>(() => {
    const readSheets = (): LandRegistrationSheetDTO[] => {
      const persisted = usePfStore.getState().productConfigs[key] as Partial<PropertyInfoDraftDTO> | undefined
      return (persisted?.landRegistrationSheets?.length
        ? persisted.landRegistrationSheets
        : [makeBlankSheet()]
      ).map(normalizeSheet)
    }

    const writeSheets = (sheets: LandRegistrationSheetDTO[]) => patch({ landRegistrationSheets: sheets })

    const mapSheet = (
      sheetIndex: number,
      fn: (sheet: LandRegistrationSheetDTO) => LandRegistrationSheetDTO
    ) => writeSheets(readSheets().map((sheet, idx) => (idx === sheetIndex ? fn(sheet) : sheet)))

    const mapFloor = (
      sheetIndex: number,
      floorIndex: number,
      fn: (floor: LandRegistrationSheetFloorDTO) => LandRegistrationSheetFloorDTO
    ) => mapSheet(sheetIndex, (sheet) => ({
      ...sheet,
      floors: sheet.floors.map((floor, idx) => (idx === floorIndex ? fn(floor) : floor)),
    }))

    return {
      addSheet: () => writeSheets([...readSheets(), makeBlankSheet()]),

      removeSheet: (sheetIndex) => writeSheets(readSheets().filter((_, idx) => idx !== sheetIndex)),

      updateSheet: (sheetIndex, updates) => mapSheet(sheetIndex, (sheet) => ({ ...sheet, ...updates })),

      addFloor: (sheetIndex) => mapSheet(sheetIndex, (sheet) => ({
        ...sheet,
        floors: [...sheet.floors, makeBlankFloor()],
      })),

      removeFloor: (sheetIndex, floorIndex) => mapSheet(sheetIndex, (sheet) => ({
        ...sheet,
        floors: sheet.floors.filter((_, idx) => idx !== floorIndex),
      })),

      updateFloor: (sheetIndex, floorIndex, updates) =>
        mapFloor(sheetIndex, floorIndex, (floor) => ({ ...floor, ...updates })),

      addParcel: (sheetIndex, floorIndex) =>
        mapFloor(sheetIndex, floorIndex, (floor) => ({
          ...floor,
          parcels: [...floor.parcels, { number: '' }],
        })),

      removeParcel: (sheetIndex, floorIndex, parcelIndex) =>
        mapFloor(sheetIndex, floorIndex, (floor) => ({
          ...floor,
          parcels: floor.parcels.filter((_, idx) => idx !== parcelIndex),
        })),

      updateParcel: (sheetIndex, floorIndex, parcelIndex, updates) =>
        mapFloor(sheetIndex, floorIndex, (floor) => ({
          ...floor,
          parcels: floor.parcels.map((parcel, idx) => (idx === parcelIndex ? { ...parcel, ...updates } : parcel)),
        })),

      setSheetsFromMap: (grouped) => {
        if (!grouped || Object.keys(grouped).length === 0) {
          patch({ landRegistrationSheets: [makeBlankSheet()], dataSource: AuthoritiesDocumentDataSource.MAP })
          return
        }
        const sheets: LandRegistrationSheetDTO[] = Object.entries(grouped).map(([district, floorSections]) => ({
          name: '',
          district,
          floors: Object.entries(floorSections).map(([section, parcels]) => ({
            hallway: section === 'undefined' || section === '-' ? '' : section,
            parcels: parcels.map((number) => ({ number })),
          })),
        }))
        patch({ landRegistrationSheets: sheets, dataSource: AuthoritiesDocumentDataSource.MAP })
      },
    }
  }, [key, patch])

  return { value, patch, sheetOps, isComplete }
}
